// ============================================================================
// ConsentTermGenerator.js — v1.0 — UU-08 Fase F GAP-3
// Geração de PDF do Termo de Consentimento Informado com jsPDF (lazy-load)
// Padrão institucional (#20243B / #C5A059) alinhado com ContractGenerator.js
// Retorna Blob pronto para upload no Firebase Storage.
// ============================================================================

function fmtDateTime(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return String(iso || ''); }
}

function truncateHash(hash) {
  const h = String(hash || '').trim();
  if (!h) return '—';
  if (h.length <= 20) return h;
  return `${h.slice(0, 16)}…${h.slice(-16)}`;
}

// ── generateConsentTermPDF ──────────────────────────────────────────────────
// Returns: Promise<Blob>
export async function generateConsentTermPDF({
  clientName,
  clientId,
  documentTitle,
  documentId,
  documentHash,
  documentCategory,
  consentDecision = 'accepted',
  consentTimestamp,
  actionType = 'informed_consent', // 'signature' | 'petition_consent' | 'informed_consent'
  userAgent,
  ipNote,
}) {
  const { jsPDF } = await import('jspdf');

  const BRAND_DARK = [32, 36, 59];     // #20243B
  const BRAND_GOLD = [197, 160, 89];   // #C5A059
  const GRAY_MID   = [100, 100, 110];
  const GRAY_LIGHT = [230, 230, 234];

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 18;

  // ── Header institucional (faixa azul + dourado) ─────────────────────────
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, W, 38, 'F');

  doc.setTextColor(...BRAND_GOLD);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('RODRIGO DEMETERCO ADVOCACIA', M, 14);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('OAB/PR n\u00ba 113.938  \u2022  rodrigo@rodrigodemeterco.adv.br', M, 21);
  doc.text('Curitiba/PR  \u2022  Portal Jur\u00eddico RDA  \u2022  Conformidade LGPD', M, 27);

  // ── Gold Rule ───────────────────────────────────────────────────────────
  doc.setDrawColor(...BRAND_GOLD);
  doc.setLineWidth(0.8);
  doc.line(M, 33, W - M, 33);

  // ── Título do Termo ─────────────────────────────────────────────────────
  doc.setTextColor(...BRAND_DARK);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  const titleMap = {
    signature: 'TERMO DE ASSINATURA ELETR\u00d4NICA',
    petition_consent: 'TERMO DE CONSENTIMENTO \u2014 PETI\u00c7\u00c3O INICIAL',
    informed_consent: 'TERMO DE CONSENTIMENTO INFORMADO',
  };
  doc.text(titleMap[actionType] || titleMap.informed_consent, W / 2, 48, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  doc.text(`Registrado em: ${fmtDateTime(consentTimestamp)}`, W / 2, 54, { align: 'center' });

  // ── Helpers de seção ────────────────────────────────────────────────────
  let y = 64;
  const sectionTitle = (title) => {
    doc.setFillColor(...GRAY_LIGHT);
    doc.rect(M, y - 4, W - M * 2, 7, 'F');
    doc.setTextColor(...BRAND_DARK);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), M + 2, y + 0.5);
    y += 10;
  };

  const row = (label, value) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GRAY_MID);
    doc.text(`${label}:`, M, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND_DARK);
    const lines = doc.splitTextToSize(String(value || '\u2014'), W - M * 2 - 48);
    doc.text(lines, M + 48, y);
    y += Math.max(6, lines.length * 5.5);
  };

  // ── 1. Identificação do Consentente ─────────────────────────────────────
  sectionTitle('1. Identifica\u00e7\u00e3o do Consentente');
  row('Nome', clientName || '\u2014');
  row('ID do Cliente', clientId || '\u2014');
  y += 3;

  // ── 2. Documento Referenciado ───────────────────────────────────────────
  sectionTitle('2. Documento Referenciado');
  row('T\u00edtulo', documentTitle || '\u2014');
  row('Categoria', documentCategory || '\u2014');
  row('ID do Documento', documentId || '\u2014');
  row('Hash SHA-256', truncateHash(documentHash));
  y += 3;

  // ── 3. Natureza do Ato ──────────────────────────────────────────────────
  sectionTitle('3. Natureza do Ato');
  const natureMap = {
    signature: 'Assinatura eletr\u00f4nica registrada via upload autenticado no Portal do Cliente.',
    petition_consent: 'Consentimento informado para protocolo judicial da pe\u00e7a acima identificada.',
    informed_consent: 'Consentimento informado sobre tratamento de dados e ci\u00eancia do conte\u00fado do documento.',
  };
  const decisionLabel = String(consentDecision).toLowerCase() === 'declined' ? 'RECUSADO' : 'ACEITO';
  row('Decis\u00e3o', decisionLabel);
  row('Tipo de A\u00e7\u00e3o', natureMap[actionType] || natureMap.informed_consent);
  y += 3;

  // ── 4. Registro de Autenticidade ────────────────────────────────────────
  sectionTitle('4. Registro de Autenticidade');
  row('Data/Hora', fmtDateTime(consentTimestamp));
  row('User-Agent', String(userAgent || '\u2014').slice(0, 90));
  row('Nota de Origem', ipNote || 'A\u00e7\u00e3o autenticada via Portal do Cliente RDA');
  y += 3;

  // ── 5. Declaração LGPD ──────────────────────────────────────────────────
  if (y > 215) { doc.addPage(); y = 20; }
  sectionTitle('5. Declara\u00e7\u00e3o de Conformidade LGPD');
  const declaration = actionType === 'signature'
    ? 'O(A) signat\u00e1rio(a) declara, sob as penas da lei, ter assinado o documento referenciado de forma livre, consciente e inequ\u00edvoca, nos termos da Lei n\u00ba 13.709/2018 (LGPD), Arts. 7\u00ba, II e 8\u00ba, \u00a7 1\u00ba. O hash SHA-256 acima garante a integridade do arquivo assinado e preserva rastreabilidade probat\u00f3ria.'
    : actionType === 'petition_consent'
      ? 'O(A) consentente declara ter revisado e aprovado a pe\u00e7a processual acima identificada para protocolo judicial, nos termos dos Arts. 7\u00ba, II e 8\u00ba da Lei n\u00ba 13.709/2018 (LGPD). Este consentimento constitui autoriza\u00e7\u00e3o expressa para prosseguimento do ato jur\u00eddico pelo escrit\u00f3rio outorgado.'
      : 'O(A) consentente declara ter ci\u00eancia do conte\u00fado do documento acima e consente com o tratamento dos dados pessoais nele contidos, nos termos da Lei n\u00ba 13.709/2018 (LGPD), Arts. 7\u00ba, II e 8\u00ba, \u00a7 1\u00ba. A decis\u00e3o registrada \u00e9 livre, informada e revog\u00e1vel mediante solicita\u00e7\u00e3o formal ao Controlador.';

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  const declLines = doc.splitTextToSize(declaration, W - M * 2);
  doc.text(declLines, M, y);
  y += declLines.length * 5 + 4;

  // ── 6. Valor Probatório ─────────────────────────────────────────────────
  if (y > 250) { doc.addPage(); y = 20; }
  sectionTitle('6. Valor Probat\u00f3rio');
  const probative = 'Este termo integra a cadeia de cust\u00f3dia digital do documento referenciado, em conformidade com a norma ISO/IEC 27037:2012 (Diretrizes para identifica\u00e7\u00e3o, coleta, aquisi\u00e7\u00e3o e preserva\u00e7\u00e3o de evid\u00eancias digitais). O hash SHA-256 calculado no dispositivo do usu\u00e1rio (client-side) garante integridade Zero-Trust, servindo como prova matem\u00e1tica irrefut\u00e1vel de que o conte\u00fado n\u00e3o foi alterado ap\u00f3s o registro. Qualquer modifica\u00e7\u00e3o posterior no documento original ser\u00e1 detect\u00e1vel via compara\u00e7\u00e3o de hash.';
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  const probLines = doc.splitTextToSize(probative, W - M * 2);
  doc.text(probLines, M, y);
  y += probLines.length * 5 + 4;

  // ── 7. Fundamentação Jurídica ──────────────────────────────────────────
  if (y > 230) { doc.addPage(); y = 20; }
  sectionTitle('7. Fundamenta\u00e7\u00e3o Jur\u00eddica');
  const legalBasis = [
    'Lei n\u00ba 14.063/2020 \u2014 Classifica\u00e7\u00e3o das assinaturas eletr\u00f4nicas em Simples, Avan\u00e7ada (Gov.br) e Qualificada (ICP-Brasil).',
    'STJ \u2014 REsp 2.243.445-SP \u2014 Dispensa reconhecimento de firma para documentos assinados via Gov.br, por configurar \u201cexcesso de formalismo\u201d perante garantia criptogr\u00e1fica estatal.',
    'Lei n\u00ba 13.709/2018 (LGPD) \u2014 Arts. 7\u00ba, II e 8\u00ba, \u00a7 1\u00ba \u2014 Base legal para tratamento e consentimento de dados pessoais.',
    'MP n\u00ba 2.200-2/2001 \u2014 Institui a ICP-Brasil e equipara assinaturas digitais certificadas a manuscritas.',
  ].join('\n');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_MID);
  const legalLines = doc.splitTextToSize(legalBasis, W - M * 2);
  doc.text(legalLines, M, y);
  y += legalLines.length * 4.5 + 4;

  // ── Footer institucional (rodapé azul + dourado) ────────────────────────
  const footerY = 287;
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, footerY - 3, W, 12, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(...BRAND_GOLD);
  doc.text('Termo gerado eletronicamente pelo Sistema RDA \u2014 Portal Jur\u00eddico', W / 2, footerY + 2, { align: 'center' });
  doc.setTextColor(180, 180, 190);
  doc.text(`Emitido em ${fmtDateTime(consentTimestamp)} \u2022 Dado sigiloso \u2014 uso restrito`, W / 2, footerY + 6, { align: 'center' });

  return doc.output('blob');
}
